import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const a = t.admin;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(a.loginError);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/10 text-[#F0F4F8] placeholder:text-[#94A3B8]/40 px-4 py-3.5 text-sm focus:outline-none focus:border-cyan-precision transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(600px circle at 50% 30%, rgba(0,229,255,0.10), transparent 70%)" }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md border border-white/10 bg-navy-surface/60 backdrop-blur-sm p-8 lg:p-10"
        data-testid="login-card"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 border border-cyan-precision/40 flex items-center justify-center text-cyan-precision">
            <Lock size={18} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-xl text-[#F0F4F8]">{a.loginTitle}</h1>
            <p className="text-xs text-[#94A3B8]">{a.loginSub}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-5">
          <div>
            <label className="block font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8] mb-2.5">
              {a.email}
            </label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
              placeholder="admin@litterantara.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8] mb-2.5">
              {a.password}
            </label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 font-mono-tech text-[13px] uppercase tracking-widest bg-cyan-precision text-navy px-6 py-4 hover:bg-cyan-bright transition-colors disabled:opacity-50 cyan-glow"
          >
            {loading ? a.signingIn : a.signIn}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
